import type { LucideIcon } from 'lucide-react';
import type { SalesforceAccount } from 'src/types/salesforce-account';
import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useApolloClient } from '@apollo/client';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@savvy-kit/components/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { useIsPageTitleCollapsed } from '@savvy-kit/templates/page-title';
import { isNil } from 'es-toolkit';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Briefcase, ChevronDown, ClipboardList, FileText, Phone, Plus, Smile, Sprout, Video } from 'lucide-react';
import { createPortal } from 'react-dom';
import { OVERVIEW_QUERY_NAME } from 'src/components/advisor-dashboard/account-overview/overview-query-name';
import { toDependentTargetClients } from 'src/components/advisor-dashboard/contact/household-contact-dialog/utils';
import { TrackedButton, TrackedDropdownMenuItem, TrackedIconButton } from 'src/components/tracked';

import type { TrackedClickableElementName } from '@savvy/deprecated-shared/tracking/tracked-clickable-element-name';

import type { HouseholdHeaderCreateHousehold } from './use-household-header-create-data';
import { removeNoteIdFromUrl } from '../../../account-overview/note-url-sync';
import { useHouseholdPageRevampEnabled } from '../../../account-overview/use-household-page-revamp-enabled';
import { useHouseholdHeaderCreateData } from './use-household-header-create-data';

// Lazy: the note editor's ~15 @lexical modules would otherwise load with the layout shell on every household page.
const CreateCallActivityDialog = dynamic(() => import('../../../account-overview/create-call-activity-dialog'), {
  ssr: false,
});
const CreateMeetingDialog = dynamic(() => import('../../../account-overview/create-meeting-dialog'), { ssr: false });
const NoteDetailView = dynamic(
  async () =>
    (await import('../../../account-overview/activity-timeline/v3/note-detail/note-detail-view')).NoteDetailView,
  { ssr: false },
);
// Lazy for the same reason: a static import would put MUI's date pickers, formik and yup in the layout shell.
const AddFamilyMemberDialog = dynamic(() => import('src/components/add-family-member-dialog'), { ssr: false });
const HouseholdContactDialog = dynamic(
  async () =>
    (await import('src/components/advisor-dashboard/contact/household-contact-dialog')).HouseholdContactDialog,
  { ssr: false },
);
const CreateClientOpportunityDialog = dynamic(
  async () =>
    (await import('../../../opportunities/create-opportunity/client-opportunity/create-client-opportunity-dialog'))
      .CreateClientOpportunityDialog,
  { ssr: false },
);
const LinkProfessionalContactDialog = dynamic(
  async () =>
    (await import('../../../account-overview/overview-panels/contacts-panel/link-professional-contact-dialog'))
      .LinkProfessionalContactDialog,
  { ssr: false },
);

const CREATE_LABEL = 'Create';
const ARCHIVED_REASON = 'This household is archived, so nothing new can be created for it.';
// A query error degrades to 'loading', so this copy must not promise an imminent result.
const UNAVAILABLE_REASON = 'Household details are unavailable right now.';
const NO_CONTACT_RECORD_REASON = 'This household has no client contact record to attribute a call to.';

// activity-timeline-frame mounts a note editor from each of these, so the header must not add a second.
const FRAME_NOTE_PARAMS = ['noteId', 'calendarEventId', 'dialpadCallId'] as const;

function toLinkedContactIds(
  relationships: HouseholdHeaderCreateHousehold['incomingProfessionalRelationships'],
): string[] {
  return relationships.flatMap((edge) => {
    if (edge.relationshipType !== 'serves' || edge.source.__typename !== 'CenterOfInfluenceRep') return [];
    return [edge.source.id];
  });
}

type CreateMenuItemProps = {
  readonly disabledReason: string | undefined;
  readonly elementName: TrackedClickableElementName;
  readonly Icon: LucideIcon;
  readonly label: string;
  readonly onSelect?: () => void;
};

function CreateMenuItem({ disabledReason, elementName, Icon, label, onSelect }: CreateMenuItemProps) {
  const item = (
    <TrackedDropdownMenuItem
      elementName={elementName}
      disabled={!isNil(disabledReason)}
      title={disabledReason}
      onClick={onSelect}
    >
      <Icon aria-hidden="true" />
      <span>{label}</span>
    </TrackedDropdownMenuItem>
  );

  if (isNil(disabledReason)) {
    return item;
  }

  // A disabled item takes no pointer events, so the reason needs a trigger wrapped around it.
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span className="block" tabIndex={0}>
            {item}
          </span>
        }
      />
      <TooltipContent side="left">{disabledReason}</TooltipContent>
    </Tooltip>
  );
}

type HouseholdCreateMenuProps = {
  readonly householdId: string | null | undefined;
  readonly isArchived: boolean;
};

export function HouseholdCreateMenu({ householdId, isArchived }: HouseholdCreateMenuProps) {
  const router = useRouter();
  const apolloClient = useApolloClient();
  const flags = useFlags();
  const flagMeetingCreation = flags['flagMeetingCreation'] === true;
  // The hook rather than the raw flag: the page shell reads it too, and a mismatch would put the
  // revamped panel and the legacy dialog on the same screen under `?revamp=1`.
  const isHouseholdPageRevampEnabled = useHouseholdPageRevampEnabled();
  const isCollapsed = useIsPageTitleCollapsed();

  const [showCallDialog, setShowCallDialog] = useState(false);
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);
  const [showNoteView, setShowNoteView] = useState(false);
  const [showFamilyMemberDialog, setShowFamilyMemberDialog] = useState(false);
  const [showLinkProfessionalDialog, setShowLinkProfessionalDialog] = useState(false);
  const [showOpportunityDialog, setShowOpportunityDialog] = useState(false);
  // Snapshot: the live account drops to null on a query error, and unmounting the note editor loses its unsaved edits.
  const [openedAccount, setOpenedAccount] = useState<SalesforceAccount | null>(null);
  const openedFromAddNote = useRef(false);
  const createTriggerRef = useRef<HTMLButtonElement>(null);

  const { account, household, opportunityHouseholdFrag, refetch, status } = useHouseholdHeaderCreateData({
    householdId,
    isHouseholdPageRevampEnabled,
  });

  const frameOwnsNoteEditor = FRAME_NOTE_PARAMS.some((param) => !isNil(router.query[param]));
  const addNoteRequested = !isNil(router.query['addNote']);

  const archivedReason = isArchived ? ARCHIVED_REASON : undefined;
  const unavailableReason = status === 'loading' ? UNAVAILABLE_REASON : undefined;
  const workReason = archivedReason ?? unavailableReason;
  const logCallReason = workReason ?? (status === 'ready' ? undefined : NO_CONTACT_RECORD_REASON);

  // Number(null) is 0 and Number(undefined) is NaN, either of which would reach the mutation as a
  // household id that matches nothing.
  const numericHouseholdId = Number(householdId);
  const hasValidHouseholdId = Number.isInteger(numericHouseholdId) && numericHouseholdId > 0;
  // The revamped household-contact dialog reads the household for its subtitle and dependent-target
  // options, but the legacy branch (AddFamilyMemberDialog) needs only the id — so when the revamp is
  // off, `household` is never fetched (see the query's `skip`) and that's fine here.
  const canAddHouseholdContact = hasValidHouseholdId && (!isHouseholdPageRevampEnabled || !isNil(household));
  const householdContactReason = archivedReason ?? (canAddHouseholdContact ? undefined : UNAVAILABLE_REASON);
  // Unlike household-contact, professional-contact has no legacy fallback that skips `household` — it
  // always needs the advisor's existing contacts and already-linked ids, so it can't reuse the revamp
  // shortcut above: with the revamp off, `household` is permanently null, and reusing that shortcut
  // would show this item as enabled while every click silently did nothing.
  const canAddProfessionalContact = hasValidHouseholdId && !isNil(household);
  const professionalContactReason = archivedReason ?? (canAddProfessionalContact ? undefined : UNAVAILABLE_REASON);

  const openSurface = (show: (open: boolean) => void) => {
    if (isArchived || isNil(account)) {
      return;
    }
    setOpenedAccount(account);
    show(true);
  };

  // isArchived is re-checked here rather than left to the disabled menu items: ?addNote is a
  // bookmarkable deep link, and this header renders outside InactiveHouseholdOverlayWrapper's
  // inert subtree, so an archived household would get an editor whose every autosave is rejected.
  useEffect(() => {
    if (!addNoteRequested || frameOwnsNoteEditor || isArchived || isNil(account)) {
      return;
    }
    openedFromAddNote.current = true;
    setOpenedAccount(account);
    setShowNoteView(true);
  }, [account, addNoteRequested, frameOwnsNoteEditor, isArchived]);

  const restoreTriggerFocus = () => {
    createTriggerRef.current?.focus();
  };

  const handleTaskSelect = () => {
    if (isNil(account)) {
      return;
    }
    void router.push(`/advisor/dashboard/client/${account.Id}/new-task`);
  };

  // The frame taking over unmounts our editor through the render gate, which skips handleNoteClose,
  // so without this showNoteView stays true and the editor re-mounts against a stale snapshot the
  // moment the frame's note closes.
  useEffect(() => {
    if (!frameOwnsNoteEditor) {
      return;
    }
    setShowNoteView(false);
    openedFromAddNote.current = false;
  }, [frameOwnsNoteEditor]);

  const handleNoteSelect = () => {
    if (frameOwnsNoteEditor) {
      return;
    }
    openSurface(setShowNoteView);
  };

  const handleNoteClose = () => {
    setShowNoteView(false);
    // Only a deep link this menu opened is ours to clear; noteId belongs to the timeline frame's editor.
    if (openedFromAddNote.current) {
      openedFromAddNote.current = false;
      removeNoteIdFromUrl(router);
    }
    restoreTriggerFocus();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          ref={createTriggerRef}
          render={
            isCollapsed ? (
              <TrackedIconButton
                size="sm"
                variant="secondary"
                elementName="household-header-create-menu-trigger"
                aria-label={CREATE_LABEL}
              />
            ) : (
              <TrackedButton size="sm" variant="secondary" elementName="household-header-create-menu-trigger" />
            )
          }
        >
          <Plus aria-hidden="true" />
          {!isCollapsed && (
            <>
              {CREATE_LABEL}
              <ChevronDown aria-hidden="true" />
            </>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Work</DropdownMenuLabel>
          <CreateMenuItem
            elementName="household-header-create-task-menu-item"
            disabledReason={workReason}
            Icon={ClipboardList}
            label="Task"
            onSelect={handleTaskSelect}
          />
          <CreateMenuItem
            elementName="household-header-create-note-menu-item"
            disabledReason={workReason}
            Icon={FileText}
            label="Note"
            onSelect={handleNoteSelect}
          />
          {flagMeetingCreation && (
            <CreateMenuItem
              elementName="household-header-create-meeting-menu-item"
              disabledReason={workReason}
              Icon={Video}
              label="Meeting"
              onSelect={() => {
                openSurface(setShowMeetingDialog);
              }}
            />
          )}
          <CreateMenuItem
            elementName="household-header-create-log-call-menu-item"
            disabledReason={logCallReason}
            Icon={Phone}
            label="Log call"
            onSelect={() => {
              openSurface(setShowCallDialog);
            }}
          />
          <CreateMenuItem
            elementName="household-header-create-opportunity-menu-item"
            disabledReason={workReason}
            Icon={Sprout}
            label="Opportunity"
            onSelect={() => {
              setShowOpportunityDialog(true);
            }}
          />

          <DropdownMenuSeparator />
          <DropdownMenuLabel>Add to household</DropdownMenuLabel>
          <CreateMenuItem
            elementName="household-header-create-professional-contact-menu-item"
            disabledReason={professionalContactReason}
            Icon={Briefcase}
            label="Professional contact"
            onSelect={() => {
              setShowLinkProfessionalDialog(true);
            }}
          />
          <CreateMenuItem
            elementName="household-header-create-household-contact-menu-item"
            disabledReason={householdContactReason}
            Icon={Smile}
            label="Household contact"
            onSelect={() => {
              setShowFamilyMemberDialog(true);
            }}
          />
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Siblings of the menu, never inside it: close-on-select unmounts a dialog rendered under DropdownMenu. */}
      {!isNil(openedAccount) && (
        <>
          <CreateCallActivityDialog
            account={openedAccount}
            open={showCallDialog}
            onClose={() => {
              setShowCallDialog(false);
              restoreTriggerFocus();
            }}
          />
          {flagMeetingCreation && (
            <CreateMeetingDialog
              account={openedAccount}
              open={showMeetingDialog}
              onClose={() => {
                setShowMeetingDialog(false);
                restoreTriggerFocus();
              }}
            />
          )}
          {/* Portaled to the body: PageTitle transforms the actions cluster this menu is mounted in, which
              makes that cluster the containing block for the editor's `fixed inset-0` and would clip the
              full-screen editor into the header's top-right corner. The sibling dialogs above portal
              themselves; this view renders a plain fixed element. */}
          {showNoteView &&
            !frameOwnsNoteEditor &&
            createPortal(<NoteDetailView account={openedAccount} onClose={handleNoteClose} />, document.body)}
        </>
      )}
      {/* Mounted only while open: the dialog holds the wizard step, so a mounted-but-closed one reopens mid-flow. */}
      {showOpportunityDialog && !isNil(opportunityHouseholdFrag) && (
        <CreateClientOpportunityDialog
          householdFrag={opportunityHouseholdFrag}
          open
          onClose={() => {
            setShowOpportunityDialog(false);
            restoreTriggerFocus();
          }}
        />
      )}
      {showFamilyMemberDialog &&
        canAddHouseholdContact &&
        // The legacy summary card renders alongside this menu when the revamp is off, so the old
        // dialog stays on that path rather than being churned.
        (isHouseholdPageRevampEnabled ? (
          <HouseholdContactDialog
            householdId={String(numericHouseholdId)}
            householdName={household?.name ?? null}
            clients={toDependentTargetClients(household?.primaryClient ?? null, household?.coClient ?? null)}
            refetchQueryName={OVERVIEW_QUERY_NAME}
            onClose={() => {
              setShowFamilyMemberDialog(false);
              restoreTriggerFocus();
            }}
          />
        ) : (
          <AddFamilyMemberDialog
            householdId={numericHouseholdId}
            onClose={() => {
              setShowFamilyMemberDialog(false);
              restoreTriggerFocus();
            }}
          />
        ))}
      {showLinkProfessionalDialog && canAddProfessionalContact && !isNil(household) && (
        <LinkProfessionalContactDialog
          open
          onOpenChange={(nextOpen) => {
            setShowLinkProfessionalDialog(nextOpen);
            if (!nextOpen) restoreTriggerFocus();
          }}
          householdId={String(numericHouseholdId)}
          householdName={household.name}
          advisorUserId={household.advisor?.user.id}
          contactOptions={household.advisor?.centerOfInfluenceReps ?? []}
          linkedContactIds={toLinkedContactIds(household.incomingProfessionalRelationships)}
          onLinked={() => {
            refetch();
            void apolloClient.refetchQueries({ include: [OVERVIEW_QUERY_NAME] });
          }}
        />
      )}
    </>
  );
}
